import { useCallback } from 'react';
import { useEditor, DEFAULT_TEXT_PROPS } from '../context/EditorContext';
import { pushRecentShape, isLineShape } from '../lib/shapes';
import { pickLocalFile } from '../components/editor/ObjectContextMenu';
import { insertTextAtCaret, insertHtmlAtCaret, isEditingText, selectAllInEditor } from '../lib/editingStore';
import { importTextFile, pickTextDocument } from '../lib/insertFile';
import { canHoldText } from '../lib/shapeText';
import { htmlToText, textToHtml } from '../lib/textUtils';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'sonner';
import { blockContext, blockForPage, userBlockForPage } from '../lib/buildingBlocks';
import { getCalendarPrefs } from '../lib/calendarPrefs';

const loadImageSize = (src) => new Promise((res) => {
  const img = new Image();
  img.onload = () => res({ w: img.naturalWidth || 300, h: img.naturalHeight || 200 });
  img.onerror = () => res({ w: 300, h: 200 });
  img.src = src;
});

/** Insert commands shared by the ribbon tabs, galleries and dialogs */
export const useInsert = () => {
  const { state, dispatch, rawDispatch, getCurrentPage, getSelectedObjects, openDialog } = useEditor();

  const center = useCallback((w, h) => {
    const page = getCurrentPage();
    return { x: Math.max(0, Math.round((page.width - w) / 2)), y: Math.max(0, Math.round((page.height - h) / 2)) };
  }, [getCurrentPage]);

  const startTextBoxTool = useCallback(() => {
    dispatch({ type: 'SET_ACTIVE_TOOL', payload: 'text' });
    toast.info('Click or drag on the page to draw a text box.', { id: 'tool-hint' });
  }, [dispatch]);

  const insertTextBox = useCallback((text = '', extraProps = {}, size = { w: 300, h: 80 }) => {
    const pos = center(size.w, size.h);
    dispatch({ type: 'ADD_OBJECT', payload: { type: 'textbox', ...pos, width: size.w, height: size.h, rotation: 0, properties: { ...DEFAULT_TEXT_PROPS, text, fontFamily: state.textSettings?.fontFamily || 'Inter', fontSize: state.textSettings?.fontSize || 16, ...extraProps } } });
  }, [dispatch, center, state.textSettings]);

  /** Insert ▸ WordArt: a pre-styled text box that opens for typing with its placeholder text selected. */
  const insertWordArt = useCallback((style, transform = 'none') => {
    const page = getCurrentPage();
    const w = Math.min(page.width - 80, 420);
    const h = transform === 'circle' ? w : Math.round(w * (transform !== 'none' ? 0.42 : 0.3));
    const pos = center(w, h);
    const id = uuidv4();
    dispatch({
      type: 'ADD_OBJECT',
      payload: {
        id, type: 'textbox', ...pos, width: w, height: h, rotation: 0,
        properties: { ...DEFAULT_TEXT_PROPS, text: 'Your Text Here', html: undefined, fontFamily: state.textSettings?.fontFamily || 'Outfit', fontSize: 40, fontWeight: 'bold', textAlign: 'center', verticalAlign: 'middle', autofit: transform === 'none' ? 'shrink' : 'none', ...(style?.props || {}), wordArt: { styleId: style?.id || null, transform } },
      },
    });
    setTimeout(() => {
      rawDispatch({ type: 'SET_EDITING', payload: id });
      setTimeout(() => selectAllInEditor(), 30);
    }, 0);
  }, [dispatch, rawDispatch, center, getCurrentPage, state.textSettings]);

  const startShapeTool = useCallback((shapeId) => {
    pushRecentShape(shapeId);
    if (shapeId === 'textbox') { startTextBoxTool(); return; }
    dispatch({ type: 'SET_ACTIVE_SHAPE', payload: shapeId });
    dispatch({ type: 'SET_ACTIVE_TOOL', payload: 'shape' });
    toast.info('Click or drag on the page to draw the shape.', { id: 'tool-hint' });
  }, [dispatch, startTextBoxTool]);

  const insertShape = useCallback((shapeId, size) => {
    pushRecentShape(shapeId);
    const line = isLineShape(shapeId);
    const w = size?.w || (line ? 240 : 160), h = size?.h || (line ? 24 : 120);
    const pos = center(w, h);
    dispatch({ type: 'ADD_OBJECT', payload: { type: 'shape', shapeType: shapeId, ...pos, width: w, height: h, rotation: 0, properties: { fill: line ? { type: 'none' } : { ...(state.shapeSettings?.fill || { type: 'solid', color: '#7c3aed' }) }, stroke: line ? { color: '#000000', width: 2 } : { ...(state.shapeSettings?.stroke || { color: '#000000', width: 0 }) }, cornerRadius: shapeId === 'rounded-rect' ? 12 : 0, opacity: 100, wrapStyle: 'none' } } });
  }, [dispatch, center, state.shapeSettings]);

  const insertTable = useCallback((rows = 3, cols = 3) => {
    const w = Math.min(getCurrentPage().width - 40, cols * 110), h = rows * 34;
    const pos = center(w, h);
    dispatch({ type: 'ADD_OBJECT', payload: { type: 'table', ...pos, width: w, height: h, rotation: 0, properties: { rows, cols, cells: Array.from({ length: rows }, () => Array.from({ length: cols }, () => ({ text: '' }))), fontFamily: 'Inter', fontSize: 14, color: '#000000', borderColor: '#000000', borderWidth: 1, headerRow: true, banded: false, cellPadding: 6, fill: { type: 'none' }, wrapStyle: 'none' } } });
  }, [dispatch, center, getCurrentPage]);

  const insertImageSrc = useCallback(async (src, meta = {}) => {
    const page = getCurrentPage();
    const { w, h } = await loadImageSize(src);
    const maxW = page.width * 0.6, maxH = page.height * 0.6;
    const s = Math.min(1, maxW / w, maxH / h);
    const W = Math.round(w * s), H = Math.round(h * s);
    const pos = center(W, H);
    dispatch({ type: 'ADD_OBJECT', payload: { type: 'image', ...pos, width: W, height: H, rotation: 0, properties: { src, opacity: 100, borderRadius: 0, objectFit: 'cover', wrapStyle: 'square', placeholder: false, ...meta } } });
  }, [dispatch, center, getCurrentPage]);

  const insertPictureFromFile = useCallback(() => {
    pickLocalFile((src, file) => insertImageSrc(src, { altText: file?.name || '' }));
  }, [insertImageSrc]);

  const openOnlinePictures = useCallback((mode = 'insert') => dispatch({ type: 'SHOW_IMAGE_GALLERY', payload: { mode } }), [dispatch]);

  const startPlaceholderTool = useCallback(() => {
    dispatch({ type: 'SET_ACTIVE_TOOL', payload: 'picture-placeholder' });
    toast.info('Click or drag on the page to place a picture placeholder.', { id: 'tool-hint' });
  }, [dispatch]);

  const insertObject = useCallback((obj) => {
    const { id, layer, ...rest } = obj;
    const pos = center(rest.width || 200, rest.height || 100);
    dispatch({ type: 'ADD_OBJECT', payload: { ...rest, x: rest.x ?? pos.x, y: rest.y ?? pos.y } });
  }, [dispatch, center]);

  /** Insert a starter Building Block (scheme-aware, scaled to fit the page, inserted as one editable group). */
  const insertBuildingBlock = useCallback((block) => {
    const page = getCurrentPage();
    const obj = blockForPage(block, blockContext(state, getCalendarPrefs()), page);
    dispatch({ type: 'ADD_OBJECT', payload: obj });
    toast.success(`Inserted “${block.name}”`, { description: 'Double-click the block (or Ctrl+Shift+G) to edit its parts.', id: 'block-inserted' });
  }, [dispatch, getCurrentPage, state]);

  /** Insert a block the user saved to the gallery (cloud). */
  const insertUserBlock = useCallback((saved) => {
    const page = getCurrentPage();
    dispatch({ type: 'ADD_OBJECT', payload: userBlockForPage(saved.object, page) });
    toast.success(`Inserted “${saved.name}”`, { id: 'block-inserted' });
  }, [dispatch, getCurrentPage]);

  /** Insert text: into the caret when editing, otherwise into the selected text box, otherwise a new box */
  const insertText = useCallback((text) => {
    if (isEditingText()) { insertTextAtCaret(text); return; }
    const sel = getSelectedObjects().filter((o) => o.type === 'textbox' || o.type === 'text');
    if (sel.length === 1) {
      const p = sel[0].properties || {};
      dispatch({ type: 'UPDATE_OBJECT_PROPS', payload: { ids: [sel[0].id], props: { text: (p.text || '') + text, html: p.html ? p.html + text.replace(/</g, '&lt;') : undefined } } });
      return;
    }
    insertTextBox(text, {}, { w: 240, h: 48 });
  }, [dispatch, getSelectedObjects, insertTextBox]);

  /**
   * Insert ▸ Text ▸ Insert File…: the text of a .txt / .md / .html (read here) or .docx / .doc / .rtf / .odt (converted
   * by the server) document goes to the caret when editing, else into the selected text box or shape, else into a new
   * text box that fills the page margins (Publisher's behaviour).
   */
  const insertFileContent = useCallback(({ html, text, filename, warnings = [] }) => {
    const where = (() => {
      if (isEditingText()) { insertHtmlAtCaret(html); return 'at the insertion point'; }
      const sel = getSelectedObjects().filter((o) => o.type === 'textbox' || o.type === 'text' || canHoldText(o));
      if (sel.length === 1) {
        const o = sel[0];
        const p = o.properties || {};
        const existing = p.html || (p.text ? textToHtml(p.text) : '');
        const hasText = htmlToText(existing).trim().length > 0 && !(o.type === 'textbox' && p.text === DEFAULT_TEXT_PROPS.text && !p.html);
        const merged = hasText ? `${existing}${html}` : html;
        dispatch({ type: 'UPDATE_OBJECT_PROPS', payload: { ids: [o.id], props: { html: merged, text: htmlToText(merged) } } });
        return o.type === 'shape' ? 'into the selected shape' : 'into the selected text box';
      }
      const page = getCurrentPage();
      const m = 48;
      dispatch({ type: 'ADD_OBJECT', payload: { type: 'textbox', x: m, y: m, width: Math.max(120, page.width - 2 * m), height: Math.max(80, page.height - 2 * m), rotation: 0, properties: { ...DEFAULT_TEXT_PROPS, text, html, fontFamily: state.textSettings?.fontFamily || 'Inter', fontSize: state.textSettings?.fontSize || 12, verticalAlign: 'top' } } });
      return 'into a new text box';
    })();
    const words = text.trim().split(/\s+/).filter(Boolean).length;
    toast.success(`Inserted “${filename}” ${where} (${words} word${words === 1 ? '' : 's'}).`, { id: 'insert-file', description: warnings[0] || undefined, duration: warnings.length ? 7000 : 4000 });
  }, [dispatch, getSelectedObjects, getCurrentPage, state.textSettings]);

  const insertFile = useCallback(async (fileArg) => {
    const file = fileArg || (await pickTextDocument());
    if (!file) return false;
    toast.loading(`Reading “${file.name}”…`, { id: 'insert-file' });
    try {
      const result = await importTextFile(file, { onProgress: (phase) => { if (phase === 'converting') toast.loading(`Converting “${file.name}” with LibreOffice…`, { id: 'insert-file' }); } });
      insertFileContent(result);
      return true;
    } catch (e) {
      toast.error(e?.message || 'The file could not be inserted.', { id: 'insert-file', duration: 7000 });
      return false;
    }
  }, [insertFileContent]);

  const insertPageNumber = useCallback((where = 'footer') => {
    const page = getCurrentPage();
    const y = where === 'header' ? 24 : page.height - 24 - 28;
    dispatch({ type: 'ADD_OBJECT', payload: { type: 'textbox', x: page.width / 2 - 40, y, width: 80, height: 28, rotation: 0, properties: { ...DEFAULT_TEXT_PROPS, text: '#', field: 'pageNumber', textAlign: 'center', fontSize: 12 } } });
  }, [dispatch, getCurrentPage]);

  const insertHeaderFooter = useCallback((where) => {
    const page = getCurrentPage();
    const h = 40;
    dispatch({ type: 'ADD_OBJECT', payload: { type: 'textbox', x: 48, y: where === 'header' ? 24 : page.height - 24 - h, width: page.width - 96, height: h, rotation: 0, properties: { ...DEFAULT_TEXT_PROPS, text: where === 'header' ? 'Header text' : 'Footer text', fontSize: 12, textAlign: where === 'header' ? 'left' : 'center', verticalAlign: 'middle' } } });
    if (state.viewMode !== 'master') toast.info('Tip: add headers and footers on the Master Page (View > Master Page) to repeat them on every page.', { id: 'hf-tip' });
  }, [dispatch, getCurrentPage, state.viewMode]);

  return { insertBuildingBlock, insertUserBlock, insertTextBox, insertWordArt, startTextBoxTool, startShapeTool, insertShape, insertTable, insertImageSrc, insertPictureFromFile, openOnlinePictures, startPlaceholderTool, insertObject, insertText, insertFile, insertFileContent, insertPageNumber, insertHeaderFooter, openDialog, rawDispatch };
};
